// Enhanced PDF Generation Utility for Quotation
// Created on 2026-02-07 by Farhan Khan - Branded, Colorful, Professional PDF
// Structure: Page 1 (Summary), Page 2 (Mounting Structure), Page 3 (BOM)
// NOTE: Requires jsPDF and jspdf-autotable packages
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const safe = (val) =>
  val === undefined || val === null || val === ""
    ? ""
    : val;

const toStringSafe = (val) =>
  typeof val === "object"
    ? JSON.stringify(val)
    : String(val || "");

// Company details constants
const COMPANY_NAME = "Solis Power Greentech Pvt Ltd";
const COMPANY_ADDRESS_LINE1 = "SB-10, Narayan Vihar, Ajmer Road Jaipur";
const COMPANY_ADDRESS_LINE2 = "Rajasthan - 302035";
const COMPANY_GSTIN = "GSTIN : 08ACYFS1632G1ZQ";

// Bank Details Constants
const BANK_NAME = "ICICI BANK LTD";
const ACCOUNT_NAME = "SOLISPOWER GREENTECH PVT LTD";
const ACCOUNT_NO = "361305500447";
const IFSC = "ICIC0003613";
const BRANCH = "BHANKROTA, JAIPUR";

// Brand Colors - Created on 2026-02-07 by Farhan Khan
const BRAND_PRIMARY = [54, 71, 148]; // #364794 - Dark blue
const BRAND_SECONDARY = [102, 126, 234]; // #667EEA - Light blue
const BRAND_ACCENT = [255, 193, 7]; // #FFC107 - Gold/Amber
const BRAND_SUCCESS = [40, 167, 69]; // #28A745 - Green
const BRAND_LIGHT_BG = [245, 247, 250]; // #F5F7FA - Light gray background
const BRAND_TEXT = [33, 37, 41]; // #212529 - Dark text

// Format date helper
const formatDate = (dateString) => {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";
    return date.toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' });
  } catch (error) {
    console.warn('Date formatting error:', error);
    return "";
  }
};

// Convert HTML to array of plain text lines (preserves bullets, line breaks)
const htmlToLines = (html) => {
  if (!html || typeof html !== 'string') return [];
  let text = html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<\/li>/gi, '\n')
    .replace(/<li>/gi, '• ')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>');
  return text.split('\n').map(s => s.trim()).filter(s => s.length > 0);
};

// Format currency helper - Prefix with Rs
const formatCurrency = (value) => {
  try {
    return `Rs ${Number(value || 0).toFixed(2)}`;
  } catch (error) {
    console.warn('Currency formatting error:', error);
    return 'Rs 0.00';
  }
};

// Load logo as base64 (async)
const loadLogoAsBase64 = async () => {
  try {
    const logoUrl = '/solisPowerLogo.png';
    const response = await fetch(logoUrl);
    if (!response.ok) {
      throw new Error('Logo not found');
    }
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.warn('Logo loading failed, PDF will continue without logo:', error);
    return null;
  }
};

// Load cover image as base64 (async)
const loadCoverImageAsBase64 = async () => {
  try {
    const imageUrl = 'https://i.ibb.co/VWq4cGyw/Gemini-Generated-Image-4yl6fo4yl6fo4yl6.png';
    const response = await fetch(imageUrl);
    if (!response.ok) {
      throw new Error('Cover image not found');
    }
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.warn('Cover image loading failed:', error);
    return null;
  }
};
// Load QR code as base64 (async)
const loadQRCodeAsBase64 = async () => {
  try {
    const imageUrl = 'https://i.ibb.co/TqD819x5/ssssasasasasasss.png';//QR Code Image
    const response = await fetch(imageUrl);
    if (!response.ok) {
      throw new Error('QR code image not found');
    }
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.warn('QR code loading failed:', error);
    return null;
  }
};

// Generate PDF function - Enhanced Professional Structure
// Created on 2026-02-07 by Farhan Khan
// Structure: Page 1 (Summary), Page 2 (Mounting Structure), Page 3 (BOM)
export const generateQuotationPDF = async (quotationData) => {
  if (!quotationData) {
    throw new Error('Quotation data is required');
  }

  // STEP 2: DEBUG (MANDATORY)
  console.log("MOUNTING DATA IN PDF", quotationData.mountingStructure || quotationData.mountingStructures);

  const { quotation, lineItems, bomItems, leadData, mountingStructure, mountingStructures } = quotationData;

  if (!quotation || !quotation.id) {
    console.error('PDF Generation - Invalid quotation:', quotation);
    throw new Error('Invalid quotation data: quotation object or ID is missing');
  }

  // 📝 TASK 1: Ensure Line Items are captured (Minimal Change)
  const finalLineItems = lineItems || quotation.lineItems || quotation.items || [];
  const safeLineItems = Array.isArray(finalLineItems) ? finalLineItems : [];
  const safeBomItems = Array.isArray(bomItems) ? bomItems : [];
  const safeMountingStructures = Array.isArray(mountingStructure) ? mountingStructure : (Array.isArray(mountingStructures) ? mountingStructures : []);

  // Normalize Created By name
  const createdByName = quotation.created_by_user?.name ||
    quotation.CREATEDBYNAME ||
    quotation.createdbyname ||
    quotation.created_by_name ||
    '';

  // Extract Bill To information from Lead or fallback to quotation fields
  const billToName = quotation?.lead?.name || quotation?.lead?.company_name ||
    (leadData?.firstname && leadData?.lastname
      ? `${leadData.firstname} ${leadData.lastname}`.trim()
      : (leadData?.name || quotation.customer_name || ''));
  console.log("Quatation2222", quotation, "Lead", quotationData.leadData);
  const billToAddress = safe(quotationData?.leadData?.customer_address || '');
  const billToCity = safe(quotationData?.leadData?.customer_city || quotationData.leadData?.city || quotation.customer_city || '');
  const billToState = safe(quotationData?.leadData?.customer_state || quotationData.leadData?.state || quotation.customer_state || '');
  const billToCountry = safe(quotationData?.leadData?.country || quotationData.leadData?.country || 'India');
  const billToPhone = safe(quotationData?.leadData?.phone || quotationData.leadData?.mobile || leadData?.phone || quotation.customer_mobile || '');
  const billToEmail = safe(quotationData?.leadData?.email || quotationData.leadData?.email || '');
  const billToGst = safe(quotation?.leadData?.gst_no || leadData?.gst_no || '');
  const billToProject = safe(quotation?.leadData?.project_line_items?.site_name || quotation?.customer_name || '');
  const billToProjectType = safe(quotation?.leadData?.project_line_items?.type_of_project || '');
  const quotationNo = safe(quotation.estimate_number || "Draft");
  const quotationNumber = quotationNo;
  const leadName = billToName;
  const createdBy = createdByName;
  const totalPagesFinal = 3; // Estimated base, dynamically updated by some flows or just used as placeholder

  console.log('PDF Generation - Starting:', {
    quotationId: quotation.id,
    estimateNumber: quotationNo,
    hasLeadData: !!quotation?.lead || !!leadData,
    billToName: billToName,
    billToPhone: billToPhone,
    createdByName: createdByName,
    hasLineItems: safeLineItems.length > 0,
    hasBomItems: safeBomItems.length > 0,
    hasMountingStructures: safeMountingStructures.length > 0,
    mountingStructuresCount: safeMountingStructures.length,
    lineItemsCount: safeLineItems.length,
    bomItemsCount: safeBomItems.length
  });

  try {
    const logoBase64 = await loadLogoAsBase64();
    const coverImageBase64 = await loadCoverImageAsBase64();
    const qrCodeBase64 = await loadQRCodeAsBase64();

    // Create new PDF document (A4 size)
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 8;
    const borderMargin = 5;
    const footerHeight = 12; // Standard compact height
    const headerHeightConst = footerHeight; // STRICT: Match footer height exactly
    let yPosition = margin + headerHeightConst; // Start below header area
    let totalPages = 1;
    const centerX = pageWidth / 2;

    // Helper function to draw page border (Page 2+ Vertical Bars Only)
    const drawPageBorder = (pageNum) => {
      // 🚫 Page 1 → NO BORDER
      if (pageNum === 1) return;

      doc.setDrawColor(...BRAND_PRIMARY);
      doc.setLineWidth(0.8);

      const sideMargin = borderMargin;
      const topBarY = 0; // Starts from top
      const bottomBarY = pageHeight; // Goes to bottom

      // LEFT SIDE VERTICAL BAR
      doc.line(sideMargin, topBarY, sideMargin, bottomBarY);

      // RIGHT SIDE VERTICAL BAR
      doc.line(pageWidth - sideMargin, topBarY, pageWidth - sideMargin, bottomBarY);
    };

    // Helper function to add quotation number header at top-left
    // Created on 2026-02-07 by Farhan Khan - Quotation number on all pages
    const addQuotationNumberHeader = (quotationNumber) => {
      // const headerX = margin;
      // const headerY = margin + 5; // Top-left position

      // doc.setFontSize(10);
      // doc.setFont('helvetica', 'bold');
      // doc.setTextColor(51, 51, 51); // #333 color
      // const headerText = `Quotation No: ${quotationNumber || 'Draft'}`;
      // doc.text(headerText, headerX, headerY);

      // // Reset text color for normal content
      // doc.setTextColor(...BRAND_TEXT);
    };

    // Helper function to add Compact Premium Header (Logo Left + QNo Right)
    const addHeader = (pageNum, quotationNumber) => {
      // ❌ Skip header on first page
      if (pageNum === 1) return;

      // Prevent duplicate rendering on same page
      const headerFlag = `_header_rendered_${pageNum}`;
      if (doc[headerFlag]) return;
      doc[headerFlag] = true;

      const headerBarHeight = footerHeight; // Standard 12mm
      const textY = (headerBarHeight / 2) + 1.5;

      // DRAW HEADER BACKGROUND
      doc.setFillColor(11, 31, 58); // #0B1F3A
      doc.rect(0, 0, pageWidth, headerBarHeight, 'F');

      // TEXT COLOR WHITE
      doc.setTextColor(255, 255, 255);

      // LEFT → Company Logo (Very Small + Centered Vertically)
      if (logoBase64) {
        try {
          // PROPORTIONAL SCALE: Only control width
          const logoWidth = 20;
          const logoHeight = 6;  // Proportional estimated for 12mm bar
          const logoX = margin;
          const logoY = (headerBarHeight - logoHeight) / 2; // (12-6)/2 = 3mm

          doc.addImage(logoBase64, 'PNG', logoX, logoY, logoWidth, logoHeight);
        } catch (logoError) {
          console.warn('Header logo failed:', logoError);
        }
      }

      // CENTER → Proposal Title (Compact 8.5pt)
      doc.setFontSize(8.5);
      doc.setFont('helvetica', 'bold');
      doc.text("Proposal for Solar Power Plant", pageWidth / 2, textY, { align: "center" });

      // RIGHT → Quotation Number (8.5pt)
      doc.setFontSize(8.5);
      doc.setFont('helvetica', 'normal');
      const qNum = quotationNumber || quotation?.quotation_no || quotation?.estimate_number || "Draft";
      doc.text(`Quotation No: ${qNum}`, pageWidth - margin, textY, { align: "right" });

      // RESET COLOR
      doc.setTextColor(...BRAND_TEXT);
    };

    // Helper function to add Enhanced Footer (Company | Website | Phone | Page)
    const addFooter = (pageNum, totalPages) => {
      // Prevent duplicate rendering on same page
      const footerFlag = `_footer_rendered_${pageNum}`;
      if (doc[footerFlag]) return;
      doc[footerFlag] = true;

      const footHeight = 12;
      const footY = pageHeight - footHeight;

      // DRAW FOOTER BACKGROUND (Dark Blue #0B1F3A)
      doc.setFillColor(11, 31, 58);
      doc.rect(0, footY, pageWidth, footHeight, 'F');

      doc.setFontSize(8.5);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(255, 255, 255);

      const footTextY = footY + (footHeight / 2) + 1;

      // LEFT - Company Name
      doc.setFont('helvetica', 'bold');
      doc.text("SOLISPOWER GREENTECH PVT LTD", margin, footTextY);

      // CENTER - Website
      doc.setFont('helvetica', 'normal');
      doc.text("www.solispower.in", pageWidth / 2, footTextY, { align: "center" });

      // RIGHT - Contact & Page Info
      const footRightText = `095090 54000  |  Page ${pageNum} of ${totalPages}`;
      doc.text(footRightText, pageWidth - margin, footTextY, { align: "right" });

      // RESET COLOR
      doc.setTextColor(...BRAND_TEXT);
    };

    // Helper function to check page break
    // Created on 2026-02-07 by Farhan Khan - Ensure quotation number header on new pages
    const checkPageBreak = (requiredHeight) => {
      const availableHeight = pageHeight - margin - footerHeight - yPosition;
      if (requiredHeight > availableHeight) {
        addFooter(totalPages, totalPages);
        drawPageBorder(totalPages);
        doc.addPage();
        totalPages++;
        drawPageBorder(totalPages);
        addHeader(totalPages, quotation.estimate_number || 'QUOTATION');
        yPosition = margin + headerHeightConst;
        return true;
      }
      return false;
    };

    // ============================================
    // PAGE 1: COVER PAGE
    // ============================================
    const imageSectionHeight = pageHeight * 0.5;

    // Top half image (full width, center crop)
    if (coverImageBase64) {
      try {
        const imgProps = doc.getImageProperties(coverImageBase64);
        const imgWidth = imgProps.width || 1;
        const imgHeight = imgProps.height || 1;

        // COVER FIT (no stretch, full width)
        const scale = Math.max(
          pageWidth / imgWidth,
          imageSectionHeight / imgHeight
        );

        const finalWidth = imgWidth * scale;
        const finalHeight = imgHeight * scale;

        // Center crop positioning
        const imgX = (pageWidth - finalWidth) / 2;
        const imgY = (imageSectionHeight - finalHeight) / 2;

        // Add image (overflow will be hidden by the bottom rectangle)
        // We use PNG since the new URL is a PNG
        doc.addImage(coverImageBase64, 'PNG', imgX, imgY, finalWidth, finalHeight);
      } catch (error) {
        console.warn('Cover image placement failed:', error);
        doc.setFontSize(10);
        doc.text("Image not available", pageWidth / 2, imageSectionHeight / 2, { align: "center" });
      }
    }

    // Draw white rectangle to clip/hide any image overflow in the bottom half
    doc.setFillColor(255, 255, 255);
    doc.rect(0, imageSectionHeight, pageWidth, pageHeight - imageSectionHeight, 'F');

    // Bottom Section: Quotation DETAILS
    let bottomY = imageSectionHeight + 25;

    // Heading (ALL CAPS)
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...BRAND_PRIMARY);
    doc.text("QUOTATION DETAILS", pageWidth / 2, bottomY, { align: 'center' });
    bottomY += 18;

    // Data Extraction (Aligned with User Snippet)
    const leadName = safe(leadData?.lead_name || billToName);
    const quotationNo = safe(quotation?.quotation_no || quotation?.estimate_number || 'Draft');
    const createdBy = safe(quotation?.created_by_name || createdByName);

    // Format date strictly as DD : MM : YYYY
    let formattedDate = "N/A";
    const rawDate = quotation?.quotation_date || quotation?.date || quotation?.created_at;
    if (rawDate) {
      const d = new Date(rawDate);
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = d.getFullYear();
      formattedDate = `${day} : ${month} : ${year}`;
    }

    // Single Vertical List Layout (Proper spacing to prevent overlap)
    doc.setFontSize(12);
    doc.setTextColor(...BRAND_TEXT);
    const fieldGap = 14; // professional vertical spacing
    const fieldLeftX = 50; // indented for clean alignment

    // Helper for consistent label/value rendering
    const renderField = (label, value, yPos) => {
      doc.setFont('helvetica', 'bold');
      doc.text(`${label}:`, fieldLeftX, yPos);
      doc.setFont('helvetica', 'normal');
      doc.text(String(value), fieldLeftX + doc.getTextWidth(`${label}:`) + 3, yPos);
    };

    renderField("Lead Name", leadName, bottomY);
    bottomY += fieldGap;

    renderField("Quotation No", quotationNo, bottomY);
    bottomY += fieldGap;

    renderField("Date", formattedDate, bottomY);
    bottomY += fieldGap;

    renderField("Quotation By", createdBy, bottomY);
    bottomY += fieldGap;

    if (billToProjectType) {
      renderField("Project Type", billToProjectType, bottomY);
      bottomY += fieldGap;
    }

    // Company Footer Details (Centered)
    let companyY = pageHeight - 45;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...BRAND_PRIMARY);
    doc.text('Solis Power Greentech Pvt Ltd', pageWidth / 2, companyY, { align: 'center' });
    companyY += 5;
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...BRAND_TEXT);
    const addressLine = `${COMPANY_ADDRESS_LINE1}, ${COMPANY_ADDRESS_LINE2}`;
    doc.text(addressLine, pageWidth / 2, companyY, { align: 'center' });
    companyY += 5;
    doc.text('www.solispower.in', pageWidth / 2, companyY, { align: 'center' });

    // ============================================
    // PAGE 2: QUOTATION SUMMARY
    // ============================================
    doc.addPage();
    totalPages++;
    drawPageBorder(totalPages);
    addHeader(totalPages, quotationNumber);
    addFooter(totalPages, totalPagesFinal);
    yPosition = margin + headerHeightConst + 10;

    // QUOTATION Title
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...BRAND_PRIMARY);
    const quotationTitle = 'QUOTATION';
    const titleWidth = doc.getTextWidth(quotationTitle);
    doc.text(quotationTitle, centerX - titleWidth / 2, yPosition);
    yPosition += 6;

    // --- SECTION 1: CUSTOMER & PROJECT HEADER (Tabular Layout) ---
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...BRAND_PRIMARY);

    // Header Grid Config
    const headerCol1 = margin;
    const headerCol2 = pageWidth / 2 + 10;
    const headerRowHeight = 6;
    let headerY = yPosition;

    const quotationDate = quotation?.quotation_date || quotationData?.quotation_date || "";
    const formattedQuotationDate = quotationDate
      ? new Date(quotationDate).toLocaleDateString("en-GB")
      : "";

    // Labels
    doc.text("To:", headerCol1, headerY);
    doc.text(`Date: ${formattedQuotationDate}`, headerCol2, headerY);
    headerY += headerRowHeight;

    doc.setTextColor(...BRAND_TEXT);
    const capacityStr = `${safe(quotation.capacity)} KW ${safe(quotation.phase)}`;
    const siteNameStr = safe(quotation?.project_line_items?.site_name || "");

    // Safely Construct Address (Filtering empty, null, or N/A)
    const addressParts = [billToAddress, billToCity, billToState].filter(part =>
      part && part.toLowerCase() !== "null" && part.toLowerCase() !== "n/a" && part.trim() !== ""
    );
    const fullCustomerAddress = addressParts.length > 0 ? addressParts.join(", ") : "";

    // Safely Map Phone and Email
    const safePhone = (billToPhone && billToPhone !== "null" && billToPhone !== "N/A") ? billToPhone : "";
    const safeEmail = (billToEmail && billToEmail !== "null" && billToEmail !== "N/A") ? billToEmail : "";

    // Dynamic Left Column (Customer Details)
    const customerFields = [
      { text: leadName, font: 'bold' },
      siteNameStr ? { text: siteNameStr, font: 'normal' } : null,
      fullCustomerAddress ? { text: fullCustomerAddress, font: 'normal' } : null,
      safePhone ? { text: `Contact No: ${safePhone}`, font: 'normal' } : null,
      safeEmail ? { text: `Email: ${safeEmail}`, font: 'normal' } : null
    ].filter(Boolean);

    // Fixed Right Column (Meta Details)
    const metaFields = [
      { text: "Quotation No.", font: 'bold' },
      { text: quotationNo, font: 'normal' },
      { text: "Created by", font: 'bold' },
      { text: createdBy, font: 'normal' },
      { text: "Capacity with Phase", font: 'bold' },
      { text: capacityStr, font: 'normal' }
    ];

    // Render Side-by-Side Dynamically with Row-Synchronous Auto-Wrap
    const maxRows = Math.max(customerFields.length, metaFields.length);

    for (let i = 0; i < maxRows; i++) {
      let maxLinesInRow = 1;

      if (customerFields[i]) {
        doc.setFont('helvetica', customerFields[i].font);
        const maxLeftWidth = headerCol2 - headerCol1 - 10;
        const leftLines = doc.splitTextToSize(customerFields[i].text, maxLeftWidth);
        maxLinesInRow = Math.max(maxLinesInRow, leftLines.length);
        doc.text(leftLines, headerCol1, headerY);
      }

      if (metaFields[i]) {
        doc.setFont('helvetica', metaFields[i].font);
        const maxRightWidth = pageWidth - margin - headerCol2;
        const rightLines = doc.splitTextToSize(metaFields[i].text, maxRightWidth);
        maxLinesInRow = Math.max(maxLinesInRow, rightLines.length);
        doc.text(rightLines, headerCol2, headerY);
      }

      headerY += (maxLinesInRow * headerRowHeight);
    }

    yPosition = headerY + 12;

    // --- SECTION 2: FINANCIAL LINE ITEMS TABLE (HIGH FIDELITY) ---
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...BRAND_PRIMARY);
    doc.text("Financial Line Items", margin, yPosition);
    yPosition += 6;

    // Prepare Financial Data
    const financeRows = [];
    const plantTotal = quotation.plant_cost || 0;
    const gstTotal = quotation.tax_gst || 0;
    const subsidyAmt = quotation.subsidy_amount || 0;
    const effectivePriceNet = (plantTotal + gstTotal) - subsidyAmt;
    const totalAmountPayableNow = plantTotal + gstTotal;

    financeRows.push(["Rooftop Solar Power Plant", `${capacityStr} Installation`, formatCurrency(quotation.rate_per_kw || 0), formatCurrency(plantTotal)]);
    financeRows.push(["GST", `${quotation.tax_percentage || "8.9%"}`, "", formatCurrency(gstTotal)]);
    financeRows.push(["SUBSIDY", "Net Deduction (Govt. Subsidy)", "", `- ${formatCurrency(subsidyAmt)}`]);
    financeRows.push([{ content: "Effective Price", styles: { fontStyle: 'bold' } }, "Price After Subsidy Credit", "", { content: formatCurrency(effectivePriceNet), styles: { fontStyle: 'bold' } }]);
    financeRows.push([{ content: "Total Amount", styles: { fontStyle: 'bold' } }, "Payable by Customer", "", { content: formatCurrency(totalAmountPayableNow), styles: { fontStyle: 'bold' } }]);

    autoTable(doc, {
      startY: yPosition,
      head: [['Particulars', 'Description', 'Rate /KW', 'Total amount']],
      body: financeRows,
      theme: 'grid',
      styles: { fontSize: 9, cellPadding: 3, textColor: BRAND_TEXT },
      headStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: 'bold', halign: 'center' },
      columnStyles: {
        0: { cellWidth: 50, fontStyle: 'bold' },
        1: { cellWidth: 60 },
        2: { cellWidth: 35, halign: 'right' },
        3: { cellWidth: 35, halign: 'right', fontStyle: 'bold' }
      }
    });

    yPosition = doc.lastAutoTable.finalY + 15;

    // --- SECTION 3: NOTES & BANK DETAILS WITH QR ---
    const bottomGridColW = (pageWidth - margin * 2) / 2;
    let gridSectionY = yPosition;

    // NOTES (Left)
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...BRAND_PRIMARY);
    doc.text("Notes:", margin, gridSectionY);
    gridSectionY += 6;
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...BRAND_TEXT);
    doc.text("Please refer detailed Terms & Conditions", margin, gridSectionY);
    gridSectionY += 5;
    doc.text("and Bill of Material sections on the", margin, gridSectionY);
    gridSectionY += 5;
    doc.text("following pages for technical specifications.", margin, gridSectionY);

    // BANK & QR (Right Alignment)
    let bankAlignY = yPosition;
    const bankLabelX = margin + bottomGridColW + 10;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...BRAND_PRIMARY);
    doc.text("Bank Details with QR Code", bankLabelX, bankAlignY);
    bankAlignY += 6;
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...BRAND_TEXT);
    doc.text(`Bank Name: ${BANK_NAME}`, bankLabelX, bankAlignY);
    bankAlignY += 5;
    doc.text(`Account Name: ${ACCOUNT_NAME}`, bankLabelX, bankAlignY);
    bankAlignY += 5;
    doc.text(`Account Number: ${ACCOUNT_NO}`, bankLabelX, bankAlignY);
    bankAlignY += 5;
    doc.text(`Branch: ${BRANCH}`, bankLabelX, bankAlignY);

    // QR Code Image Rendering (Positioned BELOW bank details text)
    const qrY = bankAlignY + 8; // Dynamically placed below the last text line

    if (qrCodeBase64) {
      try {
        const qrImgSize = 45; // Enhanced size for maximum scannability
        const qrX = bankLabelX; // Align left with Bank Details boundary

        let finalWidth = qrImgSize;
        let finalHeight = qrImgSize;

        // Prevent stretching by extracting actual dimensions
        try {
          const imgProps = doc.getImageProperties(qrCodeBase64);
          const aspectRatio = imgProps.width / imgProps.height;

          if (aspectRatio > 1) { // Wider
            finalHeight = qrImgSize / aspectRatio;
          } else if (aspectRatio < 1) { // Taller
            finalWidth = qrImgSize * aspectRatio;
          }
        } catch (propErr) {
          console.warn("Could not calculate aspect ratio, falling back to square");
        }

        doc.addImage(qrCodeBase64, 'PNG', qrX, qrY, finalWidth, finalHeight);
        doc.setFontSize(7);
        doc.setFont('helvetica', 'bold');
        doc.text("SCAN TO PAY", qrX + (finalWidth / 2), qrY + finalHeight + 4, { align: 'center' });
      } catch (qrErr) {
        console.warn("QR Code rendering failed:", qrErr);
      }
    } else {
      // Fallback Placeholder
      const qrSize = 25;
      const qrX = bankLabelX; // Align left with Bank Details boundary
      doc.setDrawColor(200, 200, 200);
      doc.rect(qrX, qrY, qrSize, qrSize);
      doc.setFontSize(6);
      doc.text("QR CODE NOT LOADED", qrX + 2, qrY + qrSize / 2);
    }

    // Guarantee clearance below QR code (qrY is top of QR code, size is ~45)
    yPosition = Math.max(yPosition + 20, qrY + 60);

    // --- SECTION 4: SIGNATURES ---
    const sigLineY = yPosition + 10;
    const sigLineWidth = 50;

    const leftSignX = margin + 5;
    const rightSignX = pageWidth - margin - sigLineWidth - 5;

    // 1. Draw solid signature lines
    doc.setDrawColor(...BRAND_PRIMARY);
    doc.setLineWidth(0.5);
    doc.line(leftSignX, sigLineY, leftSignX + sigLineWidth, sigLineY);
    doc.line(rightSignX, sigLineY, rightSignX + sigLineWidth, sigLineY);

    // 2. Add signature labels BELOW lines
    const labelY = sigLineY + 6;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...BRAND_PRIMARY);

    doc.text("Customer Signature", leftSignX + (sigLineWidth / 2), labelY, { align: 'center' });
    doc.text("Sales Executive", rightSignX + (sigLineWidth / 2), labelY, { align: 'center' });

    // 3. Add 'Created By' name floating ABOVE the Sales Executive line
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...BRAND_TEXT);
    doc.text(createdBy, rightSignX + (sigLineWidth / 2), sigLineY - 3, { align: 'center' });

    yPosition = labelY + 15;

    // ============================================
    // PAGE 3: BILL OF MATERIAL & MOUNTING STRUCTURE
    // ============================================
    doc.addPage();
    totalPages++;
    drawPageBorder(totalPages);
    addHeader(totalPages, quotation.estimate_number || 'QUOTATION');

    console.log("QUOTATION DATA FOR PDF", quotation);

    function groupBySection(items) {
      if (!items || !items.length) return [];
      const map = {};

      items.forEach(item => {
        const section = item.section || item.section_name || item.display_category || "General";

        if (!map[section]) {
          map[section] = {
            name: section,
            rows: []
          };
        }

        map[section].rows.push(item);
      });

      return Object.values(map);
    }

    const bomSections = groupBySection(bomItems || quotation.bomItems || []);

    const normalizeSections = (sections) => {
      return (sections || []).map(sec => ({
        ...sec,
        name: sec.name || sec.section_name || sec.roof_type || "Section",
        rows: sec.parts || sec.rows || sec.items || sec.components || []
      }));
    };

    const mountingSections = normalizeSections(safeMountingStructures || quotation.mounting_structure_sections || quotation.mounting_structure);

    console.log("BOM ITEMS", bomItems);
    console.log("GROUPED BOM", bomSections);
    console.log("MOUNTING", mountingStructures);
    console.log("NORMALIZED MOUNTING", mountingSections);

    // Make mapping setup
    const masters = quotationData.masters || {};
    const makeMap = (masters.makeList || []).reduce((acc, item) => {
      acc[item.id] = item.name;
      return acc;
    }, {});

    // Helper to format values that might be arrays OR postgres strings
    const formatBOMValue = (val) => {
      if (!val) return null;
      if (Array.isArray(val)) return val.join(", ");
      if (typeof val === "string" && val.includes("{")) {
        return val.replace(/[{}"]/g, "").split(",").map(s => s.trim()).filter(Boolean).join(", ");
      }
      return val;
    };

    // --- HELPER: RENDER BOM TABLE (8 COLUMNS) ---
    const renderBOMSectionTable = (section) => {
      const rows = section.rows || [];
      if (!rows.length) return;

      // Section Title
      doc.setFontSize(12);
      doc.setTextColor(...BRAND_TEXT);
      doc.text(section.name || "Section", 14, yPosition);
      yPosition += 6;

      // REQUIRED TABLE HEADERS (IN ORDER)
      const headers = ["Item Name", "Specs", "Category", "Size/Range", "Make", "Unit", "Qty", "Warranty"];

      const mapRow = (item) => {
        let specsValue = formatBOMValue(item.specs) || formatBOMValue(item.specification) || "-";
        if (typeof specsValue !== "string") specsValue = String(specsValue);

        return [
          item.item_name || "-",
          specsValue,
          formatBOMValue(item.category) || item.display_category || "-",
          item.size_range_capacity || "-",
          formatBOMValue(item.make) || "-",
          item.unit || "-",
          item.quantity || "-",
          item.manufacturer_warranty || "-"
        ];
      };

      const tableData = rows.map(item => mapRow(item));
      if (!tableData.length) tableData.push(["-", "-", "-", "-", "-", "-", "-", "-"]);

      autoTable(doc, {
        startY: yPosition,
        head: [headers],
        body: tableData,
        theme: "grid",
        styles: { fontSize: 8, cellPadding: 2, overflow: "linebreak", textColor: BRAND_TEXT },
        headStyles: { fillColor: [220, 220, 220], textColor: [51, 51, 51] },
        margin: { left: 14, right: 14 }
      });

      yPosition = doc.lastAutoTable.finalY + 10;
      if (yPosition > 260) {
        doc.addPage();
        totalPages++;
        drawPageBorder(totalPages);
        addHeader(totalPages, quotation.estimate_number || 'QUOTATION');
        yPosition = 30;
      }
    };

    // --- HELPER: RENDER MOUNTING STRUCTURE TABLE (7 COLUMNS) ---
    const renderMountingSectionTable = (section) => {
      const rows = section.rows || [];
      if (!rows.length) return;

      // Section Title
      let sectionTitle = section.name || "Structure";

      // Inject Combo Meta conditionally
      if (sectionTitle.toUpperCase().includes("COMBO")) {
        const comboMeta = [];
        if (section.combo_panels_rcc) comboMeta.push(`RCC: ${section.combo_panels_rcc}`);
        if (section.combo_panels_ground) comboMeta.push(`GROUND: ${section.combo_panels_ground}`);
        if (section.combo_panels_tinshed) comboMeta.push(`TINSHED: ${section.combo_panels_tinshed}`);
        if (comboMeta.length > 0) {
          sectionTitle += ` (${comboMeta.join(" | ")})`;
        }
      }

      doc.setFontSize(12);
      doc.setTextColor(...BRAND_TEXT);
      doc.text(sectionTitle, 14, yPosition);
      yPosition += 6;

      // 💥 REQUIRED HEADERS: Structure, Item Name, Specification, Size/Range, Make, Unit, Qty
      const headers = ["Structure", "Item Name", "Specification", "Size/Range", "Make", "Unit", "Qty"];

      const mapMountingRow = (item) => {
        let specsValue = formatBOMValue(item.specs) || formatBOMValue(item.specification) || "-";
        if (typeof specsValue !== "string") specsValue = String(specsValue);

        // Inject Special Structure params
        if (item.special_structure_length || item.length) {
          specsValue += ` | Length: ${item.special_structure_length || item.length}`;
        }
        if (item.cleaning_system || item.notes) {
          specsValue += ` | Notes: ${item.cleaning_system || item.notes}`;
        }
        if (item.others || item.others_text) {
          specsValue += ` | Others: ${item.others || item.others_text}`;
        }

        // Align Panel/Qty display accurately
        let displayQty = item.quantity || "-";
        if (item.no_of_panels !== undefined && item.no_of_panels !== null) {
          displayQty = `${item.no_of_panels} (Panels)`;
        } else if (item.number_of_panels !== undefined && item.number_of_panels !== null) {
          displayQty = `${item.number_of_panels} (Panels)`;
        }

        return [
          item.section || item.structure_type || section.name || "-",
          item.item_name || item.component_name || item.name || item.roof_type || "-",
          specsValue,
          item.size_range_capacity || item.size || "-",
          formatBOMValue(item.make) || "-",
          item.unit || "-",
          displayQty
        ];
      };

      const tableData = rows.map(item => mapMountingRow(item));
      if (!tableData.length) tableData.push(["-", "-", "-", "-", "-", "-", "-"]);

      autoTable(doc, {
        startY: yPosition,
        head: [headers],
        body: tableData,
        theme: "grid",
        styles: { fontSize: 8, cellPadding: 2, overflow: "linebreak", textColor: BRAND_TEXT },
        headStyles: { fillColor: [220, 220, 220], textColor: [51, 51, 51] },
        margin: { left: 14, right: 14 }
      });

      yPosition = doc.lastAutoTable.finalY + 10;
      if (yPosition > 260) {
        doc.addPage();
        totalPages++;
        drawPageBorder();
        addHeader(totalPages, quotation.estimate_number || 'QUOTATION');
        yPosition = 30;
      }
    };

    // --- 1. BILL OF MATERIAL DETAILS ---
    doc.setFontSize(14);
    doc.setTextColor(...BRAND_PRIMARY);
    doc.text("Bill of Material Details", 14, 20);

    yPosition = 30;

    if (bomSections && Array.isArray(bomSections) && bomSections.length > 0) {
      bomSections.forEach(renderBOMSectionTable);
    } else {
      doc.setFontSize(12);
      doc.setTextColor(...BRAND_TEXT);
      doc.text("No Bill of Material available", 14, yPosition);
      yPosition += 10;
    }

    // --- 2. MOUNTING STRUCTURE DETAILS (PAGE BREAK - ONLY IF DATA EXISTS) ---
    if (mountingSections && Array.isArray(mountingSections) && mountingSections.length > 0) {
      doc.addPage();
      totalPages++;
      drawPageBorder();
      addHeader(totalPages, quotation.estimate_number || 'QUOTATION');
      yPosition = 20;

      doc.setFontSize(14);
      doc.setTextColor(...BRAND_PRIMARY);
      doc.text("Mounting Structure Details", 14, yPosition);
      yPosition += 10;

      mountingSections.forEach(renderMountingSectionTable);
    }

    console.log("BOM COUNT", bomSections.length);
    console.log("MOUNTING COUNT", mountingSections.length);

    // ============================================
    // PAGE 5: TERMS & CONDITIONS (strict page break)
    // ============================================
    doc.addPage();
    totalPages++;
    drawPageBorder();
    addHeader(5, quotation.estimate_number || 'QUOTATION');
    yPosition = margin + headerHeightConst;

    const termsPageHeight = pageHeight - margin - footerHeight - yPosition;
    let estimatedTermsHeight = 8 + 6 + 5 * 20 + 6 + 6 + 10 + 6 + 6 + 2 * 8 + 6 + 6 + 2 * 8 + 6 + 6 + 10 + 6 + 6 + 10 + 6 + 6 + 10 + 6 + 6 + 10 + 6 + 6 + 10 + 6 + 6 + 2 * 8 + 6;
    if (estimatedTermsHeight > termsPageHeight) {
      doc.addPage();
      totalPages++;
      drawPageBorder();
      addHeader(totalPages, quotation.estimate_number || 'QUOTATION');
      yPosition = margin + headerHeightConst;
    }

    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...BRAND_PRIMARY);
    const termsTitle = 'TERMS & CONDITIONS';
    const termsTitleWidth = doc.getTextWidth(termsTitle);
    doc.text(termsTitle, centerX - termsTitleWidth / 2, yPosition);
    yPosition += 6;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...BRAND_PRIMARY);
    doc.text('SOLIS SCOPE', margin, yPosition);
    yPosition += 5;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...BRAND_TEXT);
    const solisScopeItems = [
      '• Material dispatch and Installation shall be started upon DISCOM approval only.',
      '• For better performance, solar panels should be cleaned by customer once in a week.',
      '• No concealed wiring shall be done by company. Customer should do concealed wiring with their wiremen where material shall be provided by Company.',
      '• After successful installation, Customer shall take care of solar plant by doing timely cleaning. If we found less generation at the time of attending complaint due to non-cleaning, we may charge you additional service charge.',
      '• There is manufacturing warranty for all electronics equipment. Company will help to claim this warranty if require.'
    ];
    solisScopeItems.forEach(item => {
      const lines = doc.splitTextToSize(item, pageWidth - (margin * 2) - 5);
      doc.text(lines, margin + 5, yPosition);
      yPosition += lines.length * 4 + 2;
    });
    yPosition += 4;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...BRAND_PRIMARY);
    doc.text('CUSTOMER SCOPE', margin, yPosition);
    yPosition += 5;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...BRAND_TEXT);
    const customerScopeText = '• Rooftop access, documents, site readiness, utilities.';
    const customerScopeLines = doc.splitTextToSize(customerScopeText, pageWidth - (margin * 2) - 5);
    doc.text(customerScopeLines, margin + 5, yPosition);
    yPosition += customerScopeLines.length * 4 + 6;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...BRAND_PRIMARY);
    doc.text('GENERAL TERMS & CONDITIONS', margin, yPosition);
    yPosition += 5;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...BRAND_TEXT);
    const generalTermsItems = [
      '• All terms and conditions are subject to change without prior notice.',
      '• Quotation validity is 1 week from date of issue.'
    ];
    generalTermsItems.forEach(item => {
      const lines = doc.splitTextToSize(item, pageWidth - (margin * 2) - 5);
      doc.text(lines, margin + 5, yPosition);
      yPosition += lines.length * 4 + 2;
    });
    yPosition += 4;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...BRAND_PRIMARY);
    doc.text('SUBSIDY CLAUSES', margin, yPosition);
    yPosition += 5;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...BRAND_TEXT);
    const subsidyItems = [
      '• Subsidy Credit: If applicable, any subsidy will be directly credited to the customer\'s account.',
      '• Delay Disclaimer: Company is not liable for government delays.'
    ];
    subsidyItems.forEach(item => {
      const lines = doc.splitTextToSize(item, pageWidth - (margin * 2) - 5);
      doc.text(lines, margin + 5, yPosition);
      yPosition += lines.length * 4 + 2;
    });
    yPosition += 4;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...BRAND_PRIMARY);
    doc.text('SERVICE & WARRANTY CLAUSE', margin, yPosition);
    yPosition += 5;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...BRAND_TEXT);
    const serviceWarrantyText = '• 5 years O&M support (fault finding, monitoring, visits).\n• O&M excludes panel cleaning and DISCOM billing issues.';
    const serviceWarrantyLines = doc.splitTextToSize(serviceWarrantyText, pageWidth - (margin * 2) - 5);
    doc.text(serviceWarrantyLines, margin + 5, yPosition);
    yPosition += serviceWarrantyLines.length * 4 + 6;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...BRAND_PRIMARY);
    doc.text('WARRANTY EXCLUSIONS', margin, yPosition);
    yPosition += 5;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...BRAND_TEXT);
    const warrantyExclusionsText = '• Force majeure, misuse, unauthorized repair, wear & tear not covered.';
    const warrantyExclusionsLines = doc.splitTextToSize(warrantyExclusionsText, pageWidth - (margin * 2) - 5);
    doc.text(warrantyExclusionsLines, margin + 5, yPosition);
    yPosition += warrantyExclusionsLines.length * 4 + 6;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...BRAND_PRIMARY);
    doc.text('FORCE MAJEURE', margin, yPosition);
    yPosition += 5;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...BRAND_TEXT);
    const forceMajeureText = '• Company is not liable for delays due to natural disasters, government actions, or circumstances beyond control.';
    const forceMajeureLines = doc.splitTextToSize(forceMajeureText, pageWidth - (margin * 2) - 5);
    doc.text(forceMajeureLines, margin + 5, yPosition);
    yPosition += forceMajeureLines.length * 4 + 6;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...BRAND_PRIMARY);
    doc.text('OPERATION & MAINTENANCE (O&M)', margin, yPosition);
    yPosition += 5;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...BRAND_TEXT);
    const omText = '• 5 years O&M support (fault finding, monitoring, visits).\n• O&M excludes panel cleaning and DISCOM billing issues.';
    const omLines = doc.splitTextToSize(omText, pageWidth - (margin * 2) - 5);
    doc.text(omLines, margin + 5, yPosition);
    yPosition += omLines.length * 4 + 6;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...BRAND_PRIMARY);
    doc.text('SCHEDULE FOR SITE COMPLETION', margin, yPosition);
    yPosition += 5;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...BRAND_TEXT);
    const scheduleItems = [
      '• Dispatch within 6 weeks.',
      '• Installation within 60–70 days.'
    ];
    scheduleItems.forEach(item => {
      const lines = doc.splitTextToSize(item, pageWidth - (margin * 2) - 5);
      doc.text(lines, margin + 5, yPosition);
      yPosition += lines.length * 4 + 2;
    });
    yPosition += 4;

    // Add borders, quotation number headers, and footers to all pages
    const totalPagesFinalActual = doc.internal.getNumberOfPages();

    for (let i = 1; i <= totalPagesFinalActual; i++) {
      doc.setPage(i);

      // RESET FLAGS for the final high-fidelity pass
      doc[`_header_rendered_${i}`] = false;
      doc[`_footer_rendered_${i}`] = false;

      // Apply Page Border (Page 2+ Sidebars)
      drawPageBorder(i);

      // Apply Global Header (Skip Page 1)
      addHeader(i, quotationNumber);

      // Apply Global Footer (All Pages)
      addFooter(i, totalPagesFinalActual);
    }

    // Save PDF
    const fileName = `Quotation_${quotation.estimate_number || 'Draft'}.pdf`;
    doc.save(fileName);

    console.log('PDF Generation - Success:', fileName);
  } catch (error) {
    console.error('PDF Generation - Critical Error:', error);
    throw error;
  }
};
